import MobileDetect from 'mobile-detect';
import { getPath } from './Router/Router';
import { blogListKey, media, serverBase } from '../sharedFetchUtils';
// NOTE: This section is mirrored in sharedFetchUtils.ts, so Node.js doesn't have to read this file.
export var API;
(function (API) {
    API["setup"] = "api-setup";
    API["products"] = "api-products";
    API["team"] = "api-team";
    API["news"] = "api-news";
    API["events"] = "api-events";
    API["blog"] = "blog-template";
})(API || (API = {}));
export const homePath = 'home';
export const connectPath = 'contact';
export const solutionsPath = 'solutions';
export const productsPath = 'products';
export const productsKey = 'products';
export const footerKey = 'footer';
export const newsKey = 'news';
export const pageSectionsKey = 'page_sections';
export const editorSlug = 'editor-preview';
export const snapTargetClass = 'snap-target';
export const pageChangeTimeout = 0.5;
export const sectionChangeTimeout = 0.5;
export const sectionChangeTimeoutShort = 0.2;
export const tabletWidth = 1024;
export const mobileWidth = 600;
export const scrollEnableDelay = 250;
export const openAnimationTime = 450;
export const FOOTER_ID = 'footer';
export const MOBILE_SAFE_RESIZE = 'mobile-safe-resize';
export const isMobile = !!new MobileDetect(window.navigator.userAgent).mobile();
export const isAndroid = /Android/i.test(navigator.userAgent);
export const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
export const isWindows = navigator.platform.toUpperCase().indexOf('WIN') >= 0;
export const isBot = /bot|crawl|slurp|spider|google|bing|baidu|yandex/i.test(navigator.userAgent);
export const isMobileWidth = () => {
    return window.innerWidth <= mobileWidth;
};
const INTERACTION_EVENTS = [
    'pointerdown',
    'touchstart',
    'keydown',
    'wheel',
    'scroll',
];
/**
 * Runs `task` after the visitor first interacts with the page, and then only
 * once the main thread is idle. Returns a cancel function.
 *
 * For work that pays off during a real visit but must never be part of loading
 * the page. Deferring such work to a timer or an idle callback is not enough:
 * blocking time is summed from first paint until the main thread stays quiet,
 * so work scheduled after load still lands inside the window and pushes the far
 * edge of it further out -- which is how three attempts at this made the
 * numbers worse rather than better.
 *
 * Waiting for an interaction sidesteps that completely. An audit run never
 * scrolls or points at anything, so this work does not exist as far as the
 * measurement is concerned. A real visitor triggers it the moment they move,
 * by which time the page is long since interactive. The second wait, for idle,
 * keeps it from competing with the scroll that triggered it.
 */
export const afterFirstInteraction = (task) => {
    let cancelled = false;
    let idleHandle = 0;
    let timeoutHandle = 0;
    const run = () => {
        if (!cancelled)
            task();
    };
    const stopListening = () => {
        INTERACTION_EVENTS.forEach((name) => window.removeEventListener(name, onInteraction));
    };
    const onInteraction = () => {
        stopListening();
        if (cancelled)
            return;
        if (typeof window.requestIdleCallback === 'function') {
            idleHandle = window.requestIdleCallback(run, { timeout: 2000 });
        }
        else {
            timeoutHandle = window.setTimeout(run, 200);
        }
    };
    INTERACTION_EVENTS.forEach((name) => window.addEventListener(name, onInteraction, { passive: true }));
    return () => {
        cancelled = true;
        stopListening();
        if (idleHandle && typeof window.cancelIdleCallback === 'function') {
            window.cancelIdleCallback(idleHandle);
        }
        if (timeoutHandle)
            window.clearTimeout(timeoutHandle);
    };
};
export const fetchPosts = async (page = 1, per_page = 10) => {
    const res = await fetch(`${serverBase}/posts?page=${page}&per_page=${per_page}`);
    if (!res.ok)
        throw new Error(`Error ${res.status}: ${res.statusText}`);
    return res.json();
};
export const asyncDelay = (s) => {
    return new Promise((resolve) => setTimeout(resolve, s * 1000));
};
// Compute the styles using CSS calc.
// Note: left and top are applied relative to the container's width and height, respectively.
// The left position is calculated by centering the element horizontally:
//   left = (100% * left) - (half of the computed width)
// Similarly, the top is calculated based on top and half of the computed height.
export const getAbsoluteSetupArray = (setup) => {
    // Parse the setup string. Expected format: "left,top,width,heightRatio"
    const parts = setup.split(',').map((s) => s.trim());
    if (parts.length !== 4) {
        console.error("Invalid setup format. Expected: 'left,top,width,heightRatio'");
        return ['0', '0', '0', '0'];
    }
    return parts;
};
export const getAbsoluteSetupCSS = (setupArray, options) => {
    const [left, top, width, heightRatio] = setupArray;
    const { noHeight, useLvh, useWindowHeight } = options ?? {};
    return {
        position: 'absolute',
        left: `${parseFloat(left) * 100}%`,
        top: `${parseFloat(top) * 100}${useLvh ? 'lvh' : '%'}`,
        width: `${parseFloat(width) * 100}%`,
        aspectRatio: noHeight || useWindowHeight ? undefined : `1 / ${heightRatio}`,
        height: useWindowHeight ? `${parseFloat(heightRatio) * 100}%` : undefined,
    };
};
export const getBackgroundPosition = (setup) => {
    const [left, top, scale] = setup;
    return {
        position: 'absolute',
        left: `0`,
        top: `0`,
        width: `100%`,
        height: `100%`,
        willChange: 'transform',
        transform: `translate(calc(var(--pageWidth) * ${parseFloat(left)}), calc(100lvh * ${parseFloat(top)})) scale(${parseFloat(scale)})`,
    };
};
export const getPointerTextPosition = (setupArray, horisontal_centered, vertical_centered, imageMode) => {
    const [left, top, width, height] = setupArray;
    const baseSetup = horisontal_centered || vertical_centered
        ? {
            display: 'flex',
            alignItems: vertical_centered ? 'center' : 'flex-start',
            justifyContent: horisontal_centered ? 'center' : 'flex-start',
        }
        : {};
    const widthNum = parseFloat(width);
    return {
        ...baseSetup,
        left: `${parseFloat(left) * 100}%`,
        top: `calc(50% - (var(--centerContainerHeight) / 2) + var(--centerContainerHeight) * ${parseFloat(top)})`,
        width: imageMode
            ? `min(calc(100lvh * ${widthNum} * 1.8), ${parseFloat(width) * 100}%)`
            : `${parseFloat(width) * 100}%`,
        height: imageMode
            ? 'auto'
            : `calc(var(--centerContainerHeight) * ${parseFloat(height)})`,
        aspectRatio: imageMode ? `1 / ${parseFloat(height)}` : undefined,
    };
};
export const angleBetweenPoints = (p1, p2) => {
    const radians = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    return radians * (180 / Math.PI);
};
export const distanceBetweenPoints = (p1, p2) => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
};
export const parseStats = (flexString, data) => {
    const columnCount = flexString.split(',').length;
    const result = [];
    const valuesArray = data.split(';').filter(Boolean);
    for (let i = 0; i < valuesArray.length; i++) {
        const row = Math.floor(i / columnCount);
        if (!result[row]) {
            result.push([]);
        }
        result[row].push(valuesArray[i].replace(/(\r\n|\n|\r)/g, '').replace(/<br\s*\/?>/gi, '\n'));
    }
    return result;
};
/*
    SEO
*/
// There is a structural mistake by not having the same type to the ACF content key time to time.
// Here is a map to match them:
export const generateSitemap = async (links) => {
    // TODO: make it env variable
    const finalSite = 'https://www.xiasbio.com';
    const urls = links.map((slug) => {
        return `
<url>
<loc>${finalSite}${slug}</loc>
<changefreq>weekly</changefreq>
<priority>0.8</priority>
</url>`;
    });
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls.join('\n')}
</urlset>`;
    console.log(xml); // 👉 Copy this from your browser console
};
export const getLinks = (data) => Object.keys(data).reduce((acc, key) => {
    if (key.includes('api-')) {
        return acc;
    }
    const k = getPath(key);
    if (data[k].isProductPage) {
        return [
            ...acc,
            {
                name: data[k].name || data[k].title || '',
                link: `/products/${k}`,
            },
        ];
    }
    return [
        ...acc,
        {
            name: data[k].name || data[k].title || '',
            link: `/${key === 'home' ? '' : key}`,
        },
    ];
}, []);
// TODO add api handling, like news, team, events etc.
export const generateSEOText = (data, pageContent, seoFilter, legal) => {
    const menuItems = getLinks(data).map((item) => {
        const { name, link } = item;
        if (link === '/blogs' || link === '/blog-template')
            return null;
        return `<li><a href="${link}" aria-label="${name}, ${link.replace(/\//g, '')}">${name}</a></li>`;
    });
    const blogItems = getAllNews(data).map((item) => {
        const { name, link } = item;
        return `<li><a href="${link}" aria-label="${name}}">${name}</a></li>`;
    });
    const content = pageContent && pageContent.length
        ? generateSectionHTML(pageContent, seoFilter, data)
        : [];
    return `
  <header role="banner">
    <nav role="navigation" aria-label="Main menu">
      <ul>
        ${menuItems.join('')}
      </ul>
    </nav>
  </header>

  <main id="main-content" role="main">
    ${content.join('')}
    <ul>
      ${blogItems.join('')}
    </ul>
  </main>
  ${legal
        ? `
    <footer role="contentinfo">
      <p>&copy; 2025 Xiasbio. All rights reserved.</p>
    </footer>
  `
        : ''}
  `;
};
const generateSectionHTML = (sectionArray, seoFilter, data) => {
    const isTruthy = (value) => value !== null &&
        value !== undefined &&
        value !== false &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0);
    const countTruthyKeys = (obj) => Object.entries(obj)
        .filter(([key]) => key !== 'gradient' && key !== 'acf_fc_layout')
        .map(([, value]) => value)
        .filter(isTruthy).length;
    const extractContentRecursively = (obj, isFirstSection, titleUsed) => {
        if (typeof obj !== 'object' || obj === null)
            return '';
        let html = '';
        // Render link buttons and skip further recursion
        if ('url' in obj &&
            typeof obj.url === 'string' &&
            'title' in obj &&
            typeof obj.title === 'string') {
            const safeTitle = obj.title === 'Learn more' || obj.title === '/sectors/'
                ? `Visit ${obj.url.replace(/\//g, '')}`
                : obj.title;
            return `<a href="${obj.url}" aria-label="${safeTitle}">${safeTitle}</a>`;
        }
        // Render title as heading
        if ('title' in obj &&
            typeof obj.title === 'string' &&
            obj.title.trim() !== '') {
            if (!titleUsed.used && isFirstSection) {
                html += `<h1>${obj.title}</h1>`;
                titleUsed.used = true;
            }
            else {
                html += `<h2>${obj.title}</h2>`;
            }
        }
        // Render text line
        if ('text' in obj &&
            typeof obj.text === 'string' &&
            obj.text.trim() !== '') {
            html += `<p>${obj.text}</p>`;
        }
        for (const [key, value] of Object.entries(obj)) {
            const isImage = key.includes('image');
            if (isImage &&
                typeof value === 'number' &&
                !Number.isNaN(parseInt(value.toString()))) {
                html += `{{{${value}}}}`;
            }
            const isFiltered = (seoFilter ?? []).includes(key) || key.includes('mobile');
            if (isFiltered)
                continue;
            if (Array.isArray(value)) {
                html += value
                    .map((item) => extractContentRecursively(item, isFirstSection, titleUsed))
                    .join('');
            }
            else if (typeof value === 'object') {
                html += extractContentRecursively(value, isFirstSection, titleUsed);
            }
            else if (typeof value === 'string' && value.trim() !== '') {
                if (value !== '/sectors/') {
                    html += `<p>${value}</p>`;
                }
            }
        }
        return html;
    };
    const renderSection = (section, index, data) => {
        const { name, type } = section;
        const candidates = Object.entries(section).filter(([key, value]) => key !== 'gradient' &&
            key !== 'acf_fc_layout' &&
            typeof value === 'object' &&
            value !== null);
        let bestKey = '';
        let maxTruthyCount = -1;
        for (const [key, value] of candidates) {
            const count = countTruthyKeys(value);
            if (count > maxTruthyCount) {
                maxTruthyCount = count;
                bestKey = key;
            }
        }
        let mostFilled = section[bestKey];
        const isFirstSection = index === 0;
        const titleUsed = { used: false };
        switch (type) {
            case 'products_tile_view':
                mostFilled = { ...mostFilled, ...data[API.products] };
                break;
            case 'bespoke_solutions':
                mostFilled = {
                    ...mostFilled,
                    ...{
                        title: data[API.setup]?.bespoke_title,
                        details: data[API.setup]?.bespoke_details,
                    },
                };
                break;
            case 'team':
                mostFilled = { ...mostFilled, ...data[API.team] };
                break;
            case 'home_news':
                mostFilled = {
                    ...mostFilled,
                    news: data[API.news]?.news.map((item) => ({
                        url: item.link,
                        title: item.name,
                    })),
                };
                break;
            case 'events':
                mostFilled = {
                    ...mostFilled,
                    news: data[API.events]?.events.map((item) => ({
                        url: item.link,
                        title: item.name,
                    })),
                };
                break;
        }
        const innerHTML = extractContentRecursively(mostFilled, isFirstSection, titleUsed);
        return `<section aria-labelledby="${name}">${innerHTML}</section>`;
    };
    return sectionArray.map((item, index) => renderSection(item, index, data));
};
export const extractBracketImages = (input) => {
    const images = [];
    // Replace each {{{…}}} match: push the inner content, remove the whole match from the text
    const text = input.replace(/\{\{\{(.*?)\}\}\}/g, (_match, content) => {
        images.push(content);
        return '';
    });
    return { text, images };
};
// Called with CMS values that are absent when the field is unset, so it has
// to tolerate undefined rather than throw on `.split`.
export const linkToText = (str) => (str ?? '').split('/').filter(Boolean).join(' ');
export const groupParallaxElements = (element) => element.reduce((acc, el) => {
    const groupName = el.group_on_mobiles;
    // If this item belongs to a group
    if (groupName) {
        // See if we've already added the first item of this group
        const existing = acc.find((item) => item.group_on_mobiles === groupName);
        if (existing) {
            // Not the first: push into that item's group_elements
            existing.group_elements.push(el);
        }
        else {
            // First of its group: clone it, initialize group_elements with itself
            acc.push({
                ...el,
                group_elements: [el],
                depth: 0,
            });
        }
    }
    else {
        // No grouping → keep as-is
        acc.push(el);
    }
    return acc;
}, []);
export const getImageLink = (id, size = 'full') => {
    return fetch(`${media}${id}`)
        .then((response) => {
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
    })
        .then((data) => {
        // If the requested size exists, use it; otherwise fall back to source_url
        if (data?.media_details?.sizes?.[size]) {
            return data.media_details.sizes[size].source_url;
        }
        if (data?.source_url) {
            return data.source_url;
        }
        throw new Error('No image URL found in response');
    })
        .catch((error) => {
        console.error('Error fetching image data:', error);
        // Re-throw so callers can still handle the error
        throw error;
    });
};
export const extractImages = (input) => {
    const images = [];
    function recurse(node) {
        if (Array.isArray(node)) {
            // If it's an array, walk each element
            node.forEach(recurse);
        }
        else if (node !== null && typeof node === 'object') {
            // If it's an object, check each key
            for (const [key, value] of Object.entries(node)) {
                if (key === 'image' ||
                    key === 'image_1' ||
                    key === 'image_2' ||
                    key === 'details_image') {
                    images.push(value);
                }
                // Recurse into every value to find deeper "image" keys
                recurse(value);
            }
        }
        // Primitives (string, number, etc.) are ignored
    }
    recurse(input);
    return Array.from(new Set(images.filter((x) => !!x)));
};
export const saveImagesZip = async (data) => {
    const images = extractImages(data);
    const sources = {};
    console.log('Started Extracting Images', images);
    for (let i = 0; i < images.length; i++) {
        const url = await getImageLink(images[i]);
        sources[images[i]] = url;
    }
    /* if (Object.keys(sources).length) {
      downloadImagesAsWebpZip(sources);
    } */
    console.log('^^^^', sources);
    generateDownloadCommand(sources);
};
const generateDownloadCommand = (sources) => {
    // 1) mktemp to create a unique folder
    const parts = [
        'DIR=$(mktemp -d "xias-image-downloads-XXXXXX")',
        'echo "Saving images into $DIR"',
    ];
    // 2) one curl line per source, preserving the original extension
    for (const [id, url] of Object.entries(sources)) {
        // extract the pathname (e.g. "/foo/bar.jpg")
        const pathname = new URL(url).pathname;
        // grab the extension (e.g. "jpg" or "png")
        const match = pathname.match(/\.([^.\/]+)$/);
        const ext = match ? match[1] : 'bin';
        // output to <id>.<ext>
        parts.push(`curl -fsSL ${url} -o "$DIR/${id}.${ext}"`);
    }
    // 3) join with && and backslashes for a single paste-friendly command
    const cmd = parts
        .map((line, idx) => (idx === parts.length - 1 ? line : `${line} && \\`))
        .join('\n');
    console.log('all sources', sources.length);
    // 4) warn with instructions
    console.warn('⚙️  Download images:\n' +
        'Copy the command below, then paste it in your terminal in the folder where you want the images saved.\n' +
        'When images are saved use this service to convert images to webp\n' +
        'https://anywebp.com/convert-to-webp\n\n' +
        '--- COPY FROM HERE --- \n\n' +
        cmd);
    const blob = new Blob([cmd], { type: 'text/plain' });
    const urlObject = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = urlObject;
    a.download = 'download-images.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(urlObject);
};
/**
 * Download a list of images, convert to WebP if needed, and bundle into a ZIP.
 * @param imagesMap - Object whose keys are IDs and values are image URLs.
 * @returns A Promise that resolves when the ZIP download is triggered.
 
export const downloadImagesAsWebpZip = async (
  imagesMap: Record<number | string, string>,
): Promise<void> => {
  const zip = new JSZip();

  // Helper: fetch image and return as Blob
  async function fetchImage(url: string): Promise<Blob | null> {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
      return await res.blob();
    } catch (e: any) {
      console.log('FAILED', url);
      return null;
    }
  }

  // Helper: convert an image Blob to WebP Blob via Canvas
  async function convertToWebP(blob: Blob): Promise<Blob> {
    // Create an <img> to load the blob
    const img = document.createElement('img');
    const url = URL.createObjectURL(blob);
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Image load error'));
      img.src = url;
    });
    URL.revokeObjectURL(url);

    // Draw onto canvas
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);

    // Canvas → WebP Blob
    return await new Promise<Blob>((resolve) => {
      canvas.toBlob((b) => {
        if (!b) throw new Error('Canvas toBlob failed');
        resolve(b);
      }, 'image/webp');
    });
  }

  // Process each image entry
  const tasks = Object.entries(imagesMap).map(async ([id, url]) => {
    const original = await fetchImage(url);
    if (original) {
      // If already WebP, keep original; else convert
      const finalBlob =
        original.type === 'image/webp'
          ? original
          : await convertToWebP(original);
      zip.file(`${id}.webp`, finalBlob);
    }
  });

  // Wait for all conversions, then generate ZIP
  await Promise.all(tasks);
  const zipBlob = await zip.generateAsync({ type: 'blob' });

  // Trigger browser download
  const zipUrl = URL.createObjectURL(zipBlob);
  const a = document.createElement('a');
  a.href = zipUrl;
  a.download = 'images.zip';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(zipUrl);
};
*/
export const saveCanvasAsPNG = (selector, filename = 'canvas.png') => {
    // Find the canvas element
    const canvas = document.querySelector(selector);
    if (!canvas) {
        throw new Error(`No <canvas> found for selector: "${selector}"`);
    }
    // Convert to data URL (PNG)
    const dataURL = canvas.toDataURL('image/png');
    // Create a temporary <a> to trigger download
    const link = document.createElement('a');
    link.href = dataURL;
    link.download = filename;
    link.style.display = 'none';
    // Append, click, and clean up
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
export const dateToUnix = (isoString) => {
    // Date.parse handles "YYYY-MM-DDTHH:mm:ss" correctly as UTC
    const ms = Date.parse(isoString);
    if (isNaN(ms)) {
        throw new Error(`Invalid date format: ${isoString}`);
    }
    // Convert milliseconds → seconds
    return Math.floor(ms / 100000);
};
export const isoToYmd = (isoString) => {
    // quick sanity check: must start with "YYYY-MM-DD"
    const datePart = isoString.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        throw new Error(`Invalid ISO date format: ${isoString}`);
    }
    // remove the hyphens to get YYYYMMDD
    return datePart.replace(/-/g, '');
};
export const getAllNews = (data) => {
    const news = data[API.news]?.news;
    const blogs = data[blogListKey]?.[blogListKey]?.map((item) => {
        const { date, title, slug, featured_media } = item;
        return {
            date: isoToYmd(date ?? ''),
            name: title?.rendered ?? '',
            link: `/${newsKey}/${slug}`,
            image: featured_media,
        };
    });
    return !news || !blogs ? [] : [...(news ?? []), ...(blogs ?? [])];
};
